<template>
  <div class="fence-events-chart rounded-lg border border-gray-200 p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <div>
        <h3 class="text-lg font-semibold text-gray-900">
          {{ $t('FENCE_EVENTS') }}
        </h3>
        <p class="text-sm text-gray-500">
          {{ $t('FENCE_VIOLATIONS_OVER_TIME') }}
        </p>
      </div>
    </div>

    <!-- Chart -->
    <div class="chart-container">
      <apexchart
        ref="chart"
        v-if="chartOptions && chartSeries.length"
        type="line"
        height="240"
        :options="chartOptions"
        :series="chartSeries"
      />
      <div v-else class="flex items-center justify-center h-64 text-gray-500">
        {{ $t('NO_DATA') }}
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'FenceEventsChart',
  components: { apexchart: VueApexCharts },
  props: {
    // expects: { results: [ {bucket, start_bucket_date, end_bucket_date, forbid_fence_event_sum}, ... ] }
    data: { type: Object, default: () => ({ results: [] }) },
  },
  computed: {
    labels() {
      if (!this.data || !this.data.results || !this.data.results.length)
        return []
      return this.data.results.map(function (r) {
        // Format dates to be more compact and readable
        const startDate = new Date(r.start_bucket_date)
        const endDate = new Date(r.end_bucket_date)

        // Use shorter format: MM/DD
        const startFormatted = startDate.toLocaleDateString('fa-IR', {
          month: '2-digit',
          day: '2-digit',
        })
        const endFormatted = endDate.toLocaleDateString('fa-IR', {
          month: '2-digit',
          day: '2-digit',
        })

        return startFormatted + ' - ' + endFormatted
      })
    },
    chartSeries() {
      if (!this.data || !this.data.results || !this.data.results.length)
        return []
      return [
        {
          name: this.$t('FENCE_VIOLATIONS'),
          data: this.data.results.map(function (r) {
            return Number(r.forbid_fence_event_sum || 0)
          }),
        },
      ]
    },
    totalViolations() {
      if (!this.data || !this.data.results || !this.data.results.length)
        return 0
      return this.data.results.reduce(function (sum, r) {
        return sum + Number(r.forbid_fence_event_sum || 0)
      }, 0)
    },
    chartOptions() {
      if (!this.data || !this.data.results || !this.data.results.length)
        return null

      var self = this
      return {
        chart: {
          type: 'line',
          height: 200,
          toolbar: { show: false },
          animations: { enabled: true, easing: 'easeinout', speed: 500 },
          zoom: { enabled: false },
        },
        stroke: {
          curve: 'smooth',
          width: 3,
        },
        markers: {
          size: 5,
          colors: ['#DC2626'],
          strokeColors: '#ffffff',
          strokeWidth: 2,
          hover: {
            size: 7,
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: self.labels,
          labels: {
            maxHeight: 60,
            style: {
              fontSize: '11px',
              colors: '#6B7280',
              fontWeight: 400,
            },
            offsetY: 5,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false },
        },
        yaxis: {
          title: {
            text: this.$t('VIOLATIONS_COUNT') || 'Violations',
            style: { fontSize: '12px', color: '#6B7280' },
            offsetX: -50,
          },
          labels: {
            style: { fontSize: '12px', colors: '#6B7280' },
            formatter: function (val) {
              return Math.round(val)
            },
          },
          min: 0,
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            gradientToColors: ['#F87171'],
            shadeIntensity: 1,
            type: 'vertical',
            opacityFrom: 0.8,
            opacityTo: 0.1,
            stops: [0, 100, 100, 100],
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function (val, opts) {
              return (
                Math.round(val) + ' ' + (self.$t('VIOLATIONS') || 'violations')
              )
            },
          },
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const data = self.data.results[dataPointIndex]
            const violations = Number(data.forbid_fence_event_sum || 0)
            const dateRange = self.labels[dataPointIndex]

            return `
              <div class="apexcharts-tooltip-custom" style="padding: 12px; background: white; border: 1px solid #e5e7eb; border-radius: 6px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
                <div style="font-weight: 600; margin-bottom: 8px; color: #374151;">${dateRange}</div>
                <div style="display: flex; align-items: center; margin-bottom: 4px;">
                  <div style="width: 12px; height: 12px; background-color: #DC2626; border-radius: 2px; margin-left: 8px;"></div>
                  <span style="margin-left: 8px; color: #374151;">${
                    self.$t('FENCE_VIOLATIONS') || 'Fence Violations'
                  }: ${violations}</span>
                </div>
              </div>
            `
          },
        },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'center',
          floating: false,
          offsetY: 10,
        },
        grid: {
          borderColor: '#F3F4F6',
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
        },
        colors: ['#DC2626'],
        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                height: 200,
              },
              legend: {
                position: 'bottom',
              },
            },
          },
        ],
      }
    },
  },
}
</script>

<style scoped>
.fence-events-chart {
  min-height: 300px;
}
.chart-container {
  width: 100%;
}
.chart-container >>> .apexcharts-xaxis-label,
.chart-container >>> .apexcharts-yaxis-label {
  font-family: inherit;
}
</style>
